import {Constructor} from "../../global/constructor";
import {RoleCapability} from "../enums/roles/capabilities/roleCapability";
import {OrganizationRoleCapability} from "../enums/roles/capabilities/organizationRoleCapability";
import {GroupRoleCapability} from "../enums/roles/capabilities/groupRoleCapability";
import {DocumentRoleCapability} from "../enums/roles/capabilities/documentRoleCapability";
import {PortalRoleCapability} from "../enums/roles/capabilities/portalRoleCapability";
import {ICapable} from "../interfaces/capableInterfaces";
import {PeerDocumentRoleCapability} from "~/enums/roles/capabilities/peerDocumentRoleCapability";

type numberOrNumberArray = number | number[];

interface IShare {
  reference_id: string
  capabilities: DocumentRoleCapability[]
}

export default function Capable<TBase extends Constructor>(Base: TBase) {
  return class extends Base {
    parent: any;
    base_capabilities: any;
    capabilities: RoleCapability[];
    organization_capabilities: OrganizationRoleCapability[];
    group_capabilities: {[key: string]: GroupRoleCapability[]};
    document_capabilities: DocumentRoleCapability[];
    peer_document_capabilities: PeerDocumentRoleCapability[];
    portal_capabilities: PortalRoleCapability[];
    shares: IShare[];

    hasCapability(capability: numberOrNumberArray): boolean {
      if (!this.capabilities) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasCapability(cap));
      }

      return this.capabilities.includes(capability);
    }

    hasOrganizationCapability(capability: numberOrNumberArray): boolean {
      if (!this.organization_capabilities) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasOrganizationCapability(cap));
      }

      return this.organization_capabilities.includes(capability);
    }

    hasDocumentCapability(capability: numberOrNumberArray): boolean {
      if (!this.document_capabilities) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasDocumentCapability(cap));
      }

      return this.document_capabilities.includes(capability);
    }

    hasPeerDocumentCapability(capability: numberOrNumberArray): boolean {
      if (!this.peer_document_capabilities) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasPeerDocumentCapability(cap));
      }

      return this.peer_document_capabilities.includes(capability);
    }

    hasGroupCapability(capability: numberOrNumberArray, groupId: string): boolean {
      if (!this.group_capabilities || !this.group_capabilities[groupId]) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasGroupCapability(cap, groupId));
      }

      return this.group_capabilities[groupId].includes(capability);
    }

    hasPortalCapability(capability: numberOrNumberArray): boolean {
      if (!this.portal_capabilities) {
        return false;
      }
      if (capability instanceof Array) {
        return capability.some(cap => this.hasPortalCapability(cap));
      }

      return this.portal_capabilities.includes(capability);
    }

    /**
     * Check user's abilities through the user and through the user's groups if present
     * @param user
     * @param capability
     * @param share
     */
    hasShareCapability(user: ICapable, capability: numberOrNumberArray, share: IShare = null): boolean {
      let shares: IShare[]
      if (share) {
        shares = [share]
      } else {
        if (!this.shares) {
          return false;
        }
        let finders: Array<(share: IShare) => boolean> = []
        finders.push(s => s.reference_id === user.id)
        if (Array.isArray(user.group_ids)) {
          finders.push(s => user.group_ids.some(gId => gId === s.reference_id))
        }
        shares = this.shares.filter(s => finders.some(finder => finder(s)));
      }

      if (capability instanceof Array) {
        // check that at least one capability is contained in at least one share
        return capability.some(cap => {
          return shares.some(s => {
            return this.hasShareCapability(user, cap, share)
          })
        })
      }

      // check own capabilities
      if (shares.some(s => s.capabilities?.includes(capability))) {
        return true;
      }
      // check inherited capabilities (those on parent models)
      return this.parent &&
        (typeof this.parent.isAnyInHierarchy === 'function') &&
        this.parent.isAnyInHierarchy(model => model.hasShareCapability(user, capability));
    }
  };
}